/**
 * InputManager — keyboard (state table, never single blocking key events),
 * on-screen touch buttons and gamepad, all funnelled into abstract actions.
 */

export type Action = 'left' | 'right' | 'up' | 'down' | 'jump' | 'run' | 'pause' | 'restart';

export type Bindings = Record<Action, string[]>;

export const ACTION_LABELS: Record<Action, string> = {
  left: '左 LEFT',
  right: '右 RIGHT',
  up: '上 UP',
  down: '下 DOWN',
  jump: '跳跃 JUMP',
  run: '奔跑 RUN',
  pause: '暂停 PAUSE',
  restart: '重来 RESTART',
};

export const DEFAULT_BINDINGS: Bindings = {
  left: ['ArrowLeft', 'KeyA'],
  right: ['ArrowRight', 'KeyD'],
  up: ['ArrowUp', 'KeyW'],
  down: ['ArrowDown', 'KeyS'],
  jump: ['Space', 'ArrowUp', 'KeyW'],
  run: ['ShiftLeft', 'ShiftRight', 'KeyJ'],
  pause: ['KeyP', 'Escape'],
  restart: ['KeyR'],
};

const ALL_ACTIONS: Action[] = [
  'left',
  'right',
  'up',
  'down',
  'jump',
  'run',
  'pause',
  'restart',
];

/** Keys we swallow so the page never scrolls while playing. */
const SWALLOW = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Space',
  'ShiftLeft',
  'ShiftRight',
  'Tab',
]);

interface Pad {
  jump: boolean;
  run: boolean;
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  pause: boolean;
  restart: boolean;
}

export class InputManager {
  bindings: Bindings;
  /** Raw key state, keyed by KeyboardEvent.code. */
  private keys = new Set<string>();
  /**
   * Keys that went down since the last poll. On a heavily throttled frame a key can be
   * pressed *and* released between two polls; latching the press here means a quick tap
   * is never silently dropped.
   */
  private tapped = new Set<string>();
  private held: Record<Action, boolean>;
  private prevHeld: Record<Action, boolean>;
  private edgeDown: Record<Action, boolean>;
  private edgeUp: Record<Action, boolean>;
  private touch: Partial<Record<Action, boolean>> = {};
  /** Touch buttons pressed since the last poll (same latch idea as `tapped`). */
  private touchTapped = new Set<Action>();
  private pad: Pad | null = null;
  private padIndex: number | null = null;
  /** Set while a settings screen is capturing a key for rebinding. */
  captureHandler: ((code: string) => void) | null = null;
  /** Raw listeners for things outside the action map (e.g. F3 debug). */
  onRawKey: ((code: string) => void) | null = null;
  gamepadConnected = false;

  constructor(bindings: Bindings = DEFAULT_BINDINGS) {
    this.bindings = bindings;
    this.held = emptyRecord();
    this.prevHeld = emptyRecord();
    this.edgeDown = emptyRecord();
    this.edgeUp = emptyRecord();
  }

  attach(target: Window | HTMLElement = window): void {
    const t = target as Window;
    t.addEventListener('keydown', this.onKeyDown, { passive: false });
    t.addEventListener('keyup', this.onKeyUp, { passive: false });
    window.addEventListener('blur', this.releaseAll);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('gamepadconnected', this.onGamepad);
    window.addEventListener('gamepaddisconnected', this.onGamepadOut);
  }

  detach(target: Window | HTMLElement = window): void {
    const t = target as Window;
    t.removeEventListener('keydown', this.onKeyDown);
    t.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.releaseAll);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('gamepadconnected', this.onGamepad);
    window.removeEventListener('gamepaddisconnected', this.onGamepadOut);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) {
      if (SWALLOW.has(e.code)) e.preventDefault();
      return;
    }
    if (this.captureHandler) {
      e.preventDefault();
      const h = this.captureHandler;
      this.captureHandler = null;
      h(e.code);
      return;
    }
    this.keys.add(e.code);
    this.tapped.add(e.code);
    this.onRawKey?.(e.code);
    if (SWALLOW.has(e.code)) e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    if (SWALLOW.has(e.code)) e.preventDefault();
  };

  private onVisibility = (): void => {
    if (document.hidden) this.releaseAll();
  };

  private onGamepad = (): void => {
    this.gamepadConnected = true;
  };

  private onGamepadOut = (): void => {
    this.gamepadConnected = false;
    this.pad = null;
    this.padIndex = null;
  };

  /** Drop every held key/button — used on blur so the player never "sticks". */
  releaseAll = (): void => {
    this.keys.clear();
    this.tapped.clear();
    this.touch = {};
    this.pad = null;
    for (const a of ALL_ACTIONS) {
      this.held[a] = false;
      this.prevHeld[a] = false;
      this.edgeDown[a] = false;
      this.edgeUp[a] = false;
    }
  };

  setTouch(action: Action, on: boolean): void {
    if (on && !this.touch[action]) this.touchTapped.add(action);
    this.touch[action] = on;
  }

  clearTouch(): void {
    this.touch = {};
    this.touchTapped.clear();
  }

  /** Sample every source. Call exactly once per rendered frame, before updates. */
  poll(): void {
    this.pollGamepad();
    for (const a of ALL_ACTIONS) {
      this.prevHeld[a] = this.held[a];
      const codes = this.bindings[a];
      let on = false;
      let tap = this.touchTapped.has(a);
      for (let i = 0; i < codes.length; i++) {
        if (this.keys.has(codes[i])) on = true;
        if (this.tapped.has(codes[i])) tap = true;
        if (on && tap) break;
      }
      if (!on && this.touch[a]) on = true;
      if (!on && this.pad && this.pad[a as keyof Pad]) on = true;
      this.held[a] = on;
      // A press that arrived and ended inside one frame still reports both edges.
      this.edgeDown[a] = (on || tap) && !this.prevHeld[a];
      this.edgeUp[a] = !on && (this.prevHeld[a] || tap);
    }
    this.tapped.clear();
    this.touchTapped.clear();
  }

  private pollGamepad(): void {
    const nav = globalThis.navigator as Navigator | undefined;
    if (!nav || typeof nav.getGamepads !== 'function') return;
    const pads = nav.getGamepads();
    let gp: Gamepad | null = null;
    if (this.padIndex != null) gp = pads[this.padIndex] ?? null;
    if (!gp) {
      for (let i = 0; i < pads.length; i++) {
        if (pads[i]) {
          gp = pads[i];
          this.padIndex = i;
          break;
        }
      }
    }
    if (!gp) {
      this.pad = null;
      return;
    }
    this.gamepadConnected = true;
    const b = (i: number) => !!gp!.buttons[i]?.pressed;
    const ax = gp.axes[0] ?? 0;
    const ay = gp.axes[1] ?? 0;
    this.pad = {
      jump: b(0) || b(3),
      run: b(2) || b(1) || b(5) || b(7),
      left: b(14) || ax < -0.35,
      right: b(15) || ax > 0.35,
      up: b(12) || ay < -0.5,
      down: b(13) || ay > 0.5,
      pause: b(9),
      restart: b(8),
    };
  }

  /**
   * Clear press/release edges. Called after the first fixed step of a frame so a
   * single press can never be consumed by two logic steps.
   */
  consumeEdges(): void {
    for (const a of ALL_ACTIONS) {
      this.edgeDown[a] = false;
      this.edgeUp[a] = false;
    }
  }

  down(a: Action): boolean {
    return this.held[a];
  }

  pressed(a: Action): boolean {
    return this.edgeDown[a];
  }

  released(a: Action): boolean {
    return this.edgeUp[a];
  }

  /** -1, 0 or 1. Holding both directions cancels out (classic behaviour). */
  axisX(): number {
    return (this.held.right ? 1 : 0) - (this.held.left ? 1 : 0);
  }

  /** Any of the jump-ish actions (jump binding also contains up by default). */
  jumpHeld(): boolean {
    return this.held.jump;
  }

  jumpPressed(): boolean {
    return this.edgeDown.jump;
  }

  jumpReleased(): boolean {
    return this.edgeUp.jump;
  }

  /** Begin capturing the next physical key for rebinding. */
  captureKey(cb: (code: string) => void): void {
    this.captureHandler = cb;
  }
}

function emptyRecord(): Record<Action, boolean> {
  return {
    left: false,
    right: false,
    up: false,
    down: false,
    jump: false,
    run: false,
    pause: false,
    restart: false,
  };
}

/** Human readable name for a KeyboardEvent.code. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return { Left: '←', Right: '→', Up: '↑', Down: '↓' }[code.slice(5)] ?? code;
  const map: Record<string, string> = {
    Space: 'SPACE',
    ShiftLeft: 'L-SHIFT',
    ShiftRight: 'R-SHIFT',
    Escape: 'ESC',
    ControlLeft: 'L-CTRL',
    ControlRight: 'R-CTRL',
    Enter: 'ENTER',
    Tab: 'TAB',
  };
  return map[code] ?? code.toUpperCase();
}
